import React, { useEffect, useRef, useState } from "react";
import { Navigate, useLocation, useSearchParams } from "react-router-dom";
import { useMutation, useQuery } from "@apollo/client";
import { Box, Button, Flex, Spinner, Text } from "@chakra-ui/react";

import WithAppLayout from "../../../components/WithAppLayout";
import { LoadingIndicator } from "../../../components/LoadingIndicator";
import {
  BULLHORN_EMBED_ACCESS_QUERY,
  BULLHORN_JOB_IMPORT_STATUS_QUERY,
  MOBLYZE_JOB_BY_BULLHORN_ID_QUERY,
  REQUEST_BULLHORN_JOB_IMPORT_MUTATION,
} from "../../../graphql/jobs";
import { UPDATE_CURRENT_USER_AGENCY_MUTATION } from "../../../graphql/users";
import { ADD_JOB_PATH, JOB_DETAILS_PATH, PREVIEW_JOB_PATH } from "../../../routes";
import colors from "../../../helpers/colors";
import { ClientsAppUrl } from "../../../helpers/config";
import { FLAGS, useBoolFlag } from "../../../services/featureFlags";

// A job that is not live yet (auto-imported from Bullhorn or otherwise
// awaiting review) opens on the review/preview page so the recruiter can
// approve it in place; anything else opens the job details.
export function pathForBullhornJob(job) {
  const path = job.status === "Pending" ? PREVIEW_JOB_PATH : JOB_DETAILS_PATH;
  return path.replace(":id", job.id);
}

// How long we wait for the auto-import before offering the manual flow, and
// how often we poll. The pipeline is typically 20–40s (fetch, draft, judge); a
// stuck worker should not strand the recruiter. Mutable so tests can shorten
// the intervals instead of waiting on the wall clock.
export const importTiming = { waitMs: 90 * 1000, pollMs: 2500 };

// Turns an import status into what the page should do next.
//   { kind: "redirect", to }          a job exists → go there
//   { kind: "wait" }                  still importing → keep the spinner
//   { kind: "manual", reason }        nothing will be created → offer the manual flow
export function decideFromImportStatus(status) {
  if (!status) return { kind: "wait" };
  if (status.state === "Done" && status.moblyzeJobId) {
    return {
      kind: "redirect",
      to: pathForBullhornJob({ id: status.moblyzeJobId, status: status.jobStatus }),
    };
  }
  if (status.state === "InProgress") return { kind: "wait" };
  // NotFound, Skipped, Manual — or Done without a job we can see.
  return { kind: "manual", reason: status.message || null };
}

function ManualFlow({ id, reason }) {
  const publishUrl = `${ADD_JOB_PATH}?bullhornId=${encodeURIComponent(id)}`;
  return (
    <Flex direction="column" gap="16px">
      <Text fontSize="md" lineHeight="6">
        No job found for Bullhorn ID {id}.
      </Text>
      {reason ? (
        <Text fontSize="sm" color={colors.greyDark}>
          {reason}
        </Text>
      ) : null}
      <Button
        as="a"
        href={publishUrl}
        alignSelf="flex-start"
        backgroundColor={colors.white}
        color={colors.greyDark}
        _hover={{ backgroundColor: colors.white }}
      >
        Publish new job
      </Button>
    </Flex>
  );
}

// Shown while the auto-import runs: asks for the import once, then polls the
// status until a job exists, the pipeline declines, or we give up waiting.
function ImportInProgress({ id }) {
  const [requested, setRequested] = useState(false);
  const [decision, setDecision] = useState({ kind: "wait" });
  const startedAt = useRef(Date.now());
  // React 18 StrictMode runs mount effects twice in development; the request
  // must go out once. The parent keys this component by id, so a new job
  // order gets a fresh instance (and a fresh ref).
  const requestSent = useRef(false);

  const [requestImport] = useMutation(REQUEST_BULLHORN_JOB_IMPORT_MUTATION, {
    fetchPolicy: "no-cache",
    onCompleted: (data) => {
      setRequested(true);
      setDecision(decideFromImportStatus(data?.requestBullhornJobImport));
    },
    onError: () => {
      // Could not even ask: fall back to the manual flow rather than spin.
      setRequested(true);
      setDecision({ kind: "manual", reason: null });
    },
  });

  useEffect(() => {
    if (requestSent.current) return;
    requestSent.current = true;
    requestImport({ variables: { bullhornId: id } });
  }, [id, requestImport]);

  const waiting = requested && decision.kind === "wait";
  const { data: statusData, stopPolling } = useQuery(BULLHORN_JOB_IMPORT_STATUS_QUERY, {
    variables: { bullhornId: id },
    skip: !waiting,
    pollInterval: importTiming.pollMs,
    fetchPolicy: "network-only",
    onError: () => {},
  });

  useEffect(() => {
    if (!waiting) return;
    const next = decideFromImportStatus(statusData?.bullhornJobImportStatus);
    if (next.kind !== "wait") {
      stopPolling();
      setDecision(next);
    }
  }, [waiting, statusData, stopPolling]);

  // The give-up deadline is its own timer, not a check inside the poll effect.
  // Apollo hands back the same `data` object when a poll returns an identical
  // result, so a status that never changes (the pipeline decided under another
  // agency, a dropped task) re-rendered nothing and the deadline was never
  // reached — the page spun forever. A timer fires regardless.
  useEffect(() => {
    if (!waiting) return undefined;
    const remaining = Math.max(0, importTiming.waitMs - (Date.now() - startedAt.current));
    const timer = setTimeout(() => {
      stopPolling();
      setDecision({
        kind: "manual",
        reason:
          "The automatic import is taking longer than expected. You can publish the job manually now; the import will still finish in the background.",
      });
    }, remaining);
    return () => clearTimeout(timer);
  }, [waiting, stopPolling]);

  if (decision.kind === "redirect") {
    return <Navigate to={decision.to} replace />;
  }
  if (decision.kind === "manual") {
    return <ManualFlow id={id} reason={decision.reason} />;
  }
  return (
    <Flex direction="column" align="flex-start" gap="12px" role="status">
      <Spinner size="md" />
      <Text fontSize="md" lineHeight="6">
        Importing job order {id} from Bullhorn…
      </Text>
      <Text fontSize="sm" color={colors.greyDark}>
        Reading the job order, drafting the posting and checking it. This usually takes under a minute.
      </Text>
    </Flex>
  );
}

// The tab is embedded in Bullhorn, where a recruiter cannot see the app chrome
// that would normally explain a permission problem, so say it plainly here.
// Only an owner account can move itself between agencies (the Companies page
// switcher), so only they are offered the switch — and only they are told
// which agency owns this Bullhorn tenant.
export function NoAccess({ access }) {
  const [switching, setSwitching] = useState(false);
  const [switchAgency] = useMutation(UPDATE_CURRENT_USER_AGENCY_MUTATION, {
    onCompleted: () => window.location.reload(),
    onError: () => setSwitching(false),
  });

  const agencyLabel = access.requiredAgencyName || access.requiredAgencySlugId;
  const canSwitch = access.canSwitchAgency && Boolean(access.requiredAgencySlugId);

  return (
    <Flex direction="column" gap="16px" data-testid="embed-no-access">
      <Text fontSize="md" lineHeight="6">
        {canSwitch
          ? `You need to be logged in as ${agencyLabel} to access the Moblyze tab in Bullhorn.`
          : `Your account (${access.viewerEmail}) does not have permission to view the Moblyze tab in Bullhorn. Please contact a Moblyze admin for access.`}
      </Text>
      {canSwitch && (
        <Button
          alignSelf="flex-start"
          isLoading={switching}
          loadingText={`Switching to ${agencyLabel}`}
          onClick={() => {
            setSwitching(true);
            switchAgency({ variables: { agencySlugId: access.requiredAgencySlugId } });
          }}
        >
          Switch to {agencyLabel}
        </Button>
      )}
    </Flex>
  );
}

function ExternalBullhornJobInner() {
  const [searchParams] = useSearchParams();
  const id = searchParams.get("EntityID");

  // Asked first: everything below acts on the viewer's agency, so a viewer who
  // cannot use the tab should be told why rather than shown a spinner or an
  // error. A failure here is not fatal — fall through to the normal flow.
  const { data: accessData, loading: accessLoading } = useQuery(BULLHORN_EMBED_ACCESS_QUERY, {
    fetchPolicy: "network-only",
    onError: () => {},
  });
  const access = accessData?.bullhornEmbedAccess;

  const { data, loading, error } = useQuery(MOBLYZE_JOB_BY_BULLHORN_ID_QUERY, {
    variables: { bullhornId: id },
    skip: !id || (Boolean(access) && !access.allowed),
    fetchPolicy: "network-only",
  });

  if (!id) {
    return (
      <Box>
        <Text>Missing Bullhorn EntityID parameter.</Text>
      </Box>
    );
  }

  if (accessLoading) {
    return <LoadingIndicator />;
  }

  if (access && !access.allowed) {
    return <NoAccess access={access} />;
  }

  if (loading) {
    return <LoadingIndicator />;
  }

  if (error) {
    return (
      <Box>
        <Text>Error loading job. Please try again.</Text>
        <Button onClick={() => window.location.reload()}>Retry</Button>
      </Box>
    );
  }

  const moblyzeJob = data?.moblyzeJobByBullhornId;

  if (moblyzeJob?.id) {
    return <Navigate to={pathForBullhornJob(moblyzeJob)} replace />;
  }

  // No job yet: the sync may not have seen this job order. Trigger the import
  // and follow it, instead of sending the recruiter straight to the wizard.
  return <ImportInProgress key={id} id={id} />;
}

const ExternalBullhornJobInApp = WithAppLayout(ExternalBullhornJobInner);

// Where the tab lives in the clients app: a fixed path that reads the same
// query string Bullhorn appends here (EntityID, …), so the Bullhorn tab can
// later point straight at it and this page can go.
export function clientsBullhornUrl(search) {
  return `${ClientsAppUrl}/bullhorn${search || ""}`;
}

// Outside WithAppLayout so a flag that is on for everyone sends the frame on
// before the portal asks for a sign-in. A flag targeted by role or agency
// resolves only once AppLayout has pushed the user context; the page then
// redirects from wherever it got to. An import it already requested is
// harmless: the request is idempotent, and the clients page makes the same one.
function ExternalBullhornJobPage(props) {
  const toClients = useBoolFlag(FLAGS.WEB_BULLHORN_TAB_TO_CLIENTS);
  const { search } = useLocation();

  useEffect(() => {
    if (toClients) window.location.replace(clientsBullhornUrl(search));
  }, [toClients, search]);

  if (toClients) return <LoadingIndicator />;
  return <ExternalBullhornJobInApp {...props} />;
}

export default ExternalBullhornJobPage;
